/**
 * Конвейер контента: дневной план монтажёра, загрузка роликов, свои темы.
 * Серверная часть — `supabase/functions/make-server-bde4f709/tasks.routes.tsx`.
 */

import { api } from './client';
import type { DayTask, TaskPoint } from '../../entities/task-point';

export type DivisionRole = 'head' | 'content_maker' | 'editor' | 'moderator' | 'distributor';

export interface PipelineDivision {
  id: string;
  name: string;
  slug: string;
  role: DivisionRole;
  /**
   * Можно ли выкладывать от имени аккаунтов этого отделения. Подсказка для
   * интерфейса, чтобы не предлагать кнопку, на которую придёт отказ;
   * решение всё равно принимает сервер.
   */
  canPublish: boolean;
}

export interface PipelineTeam {
  id: string;
  divisionId: string;
  name: string;
  slug: string;
  contentMakerId: string | null;
}

/**
 * Проект — то, чем отделение занимается; в проекте несколько пабликов, а у
 * паблика уже аккаунты в TikTok и Instagram. Команда и проект — разные разрезы
 * одного отделения: команда про людей, проект про работу.
 */
export interface PipelineProject {
  id: string;
  divisionId: string;
  name: string;
  slug: string;
}

export interface PipelineContext {
  user: { id: string; fullName: string | null; avatarUrl: string | null; role: 'admin' | 'employee' };
  divisions: PipelineDivision[];
  teams: PipelineTeam[];
  projects: PipelineProject[];
}

export interface MyDayResponse {
  task: DayTask | null;
  /** Что руководитель сказал отделению на этот день. */
  directive: string | null;
  /** Цель недели — чтобы тема не выглядела взявшейся ниоткуда. */
  weekly: { title: string; targetCount: number | null } | null;
  points: TaskPoint[];
}

interface UploadTicket {
  bucket: string;
  path: string;
  token: string;
  signedUrl: string;
}

export type PointEventKind =
  | 'upload' | 'rework' | 'approval' | 'backup' | 'delivery' | 'publication';

export interface PointEvent {
  kind: PointEventKind;
  at: string | null;
  title: string;
  who: string | null;
  note?: string | null;
  platform?: string;
  url?: string;
}

export interface PointVersion {
  id: string;
  version: number;
  title: string;
  durationSeconds: number | null;
  uploadedAt: string;
  uploadedBy: string | null;
  deliveryStatus: 'pending' | 'backed_up' | 'delivered' | 'failed';
}

export interface PointDetail {
  point: TaskPoint;
  task: { id: string; workDate: string | null; team: { id: string; name: string; slug: string } | null };
  versions: PointVersion[];
  events: PointEvent[];
}

export type ReviewStageName = 'moderator' | 'content_maker';

/** Строка очереди проверки и экрана публикации. */
export interface QueueRow {
  id: string;
  topic: string;
  brief: string | null;
  status: TaskPoint['status'];
  deadlineAt: string | null;
  isOwnTopic: boolean;
  waitingSince: string;
  versions: number;
  workDate: string;
  assignee: { id: string; name: string | null };
  team: { id: string; name: string | null } | null;
  video: { id: string; version: number; title: string; durationSeconds: number | null } | null;
  moderatedBy: { name: string | null; at: string } | null;
  /** Только на экране публикации. */
  links?: Partial<Record<'tiktok' | 'instagram' | 'youtube' | 'other', string>>;
  linkCount?: number;
  publishedAt?: string | null;
  /** Только в сводке руководителя. */
  responsible?: { id: string; name: string | null } | null;
}

export interface AttentionBuckets {
  overdueRework: QueueRow[];
  approvedNoLink: QueueRow[];
  stuckInQueue: QueueRow[];
  total: number;
  stuckHours: number;
}

export interface WeekEditor {
  id: string;
  name: string | null;
  teamName: string | null;
  days: Record<string, { done: number; own: number; firstTime: number; planned: number }>;
  done: number;
  own: number;
  published: number;
  planned: number;
  firstTimePercent: number;
}

export interface WeekReport {
  weekStart: string;
  weekEnd: string;
  weekly: { id: string; title: string; targetCount: number | null } | null;
  totals: {
    target: number | null;
    fact: number;
    published: number;
    remaining: number | null;
    percent: number | null;
    ownTopics: number;
    firstTimePercent: number;
  };
  editors: WeekEditor[];
}

export interface Directive {
  id: string;
  body: string;
  workDate: string;
  author: string | null;
  createdAt: string;
}

export const tasksApi = {
  getContext: () => api.get<PipelineContext>('/tasks/context'),

  getMyDay: (date: string) =>
    api.get<MyDayResponse>('/tasks/my-day', { params: { date } }),

  getPoint: (pointId: string) => api.get<PointDetail>(`/tasks/points/${pointId}`),

  // ── очередь проверки ───────────────────────────────────────────────
  getQueue: (divisionId: string, stage: ReviewStageName, teamId?: string) =>
    api.get<{ points: QueueRow[]; stage: ReviewStageName }>('/tasks/queue', {
      params: { divisionId, stage, ...(teamId ? { teamId } : {}) },
    }),

  getVideoUrl: (pointId: string, version?: number) =>
    api.get<{ url: string; version: number; title: string; durationSeconds: number | null }>(
      `/tasks/points/${pointId}/video-url`,
      version ? { params: { version: String(version) } } : undefined,
    ),

  review: (
    pointId: string,
    body: { decision: 'approved' | 'rework'; reason?: string; reasonCategory?: string; deadlineAt?: string },
  ) => api.post<{ status: string }>(`/tasks/points/${pointId}/review`, body),

  batchApprove: (pointIds: string[]) =>
    api.post<{ approved: number; skipped: { id: string; reason: string }[] }>(
      '/tasks/points/batch-review', { pointIds },
    ),

  // ── публикация ─────────────────────────────────────────────────────
  getPublications: (divisionId: string, teamId?: string) =>
    api.get<{ awaiting: QueueRow[]; published: QueueRow[] }>('/tasks/publications', {
      params: { divisionId, ...(teamId ? { teamId } : {}) },
    }),

  attachLink: (
    pointId: string,
    body: { url: string; platform: 'tiktok' | 'instagram'; accountId?: string },
  ) => api.post<{ links: number; required: number }>(`/tasks/points/${pointId}/publication`, body),

  // ── руководитель ───────────────────────────────────────────────────
  getAttention: (divisionId: string, stuckHours?: number) =>
    api.get<AttentionBuckets>('/tasks/attention', {
      params: { divisionId, ...(stuckHours ? { stuckHours: String(stuckHours) } : {}) },
    }),

  getWeek: (divisionId: string, weekStart: string, teamId?: string) =>
    api.get<WeekReport>('/tasks/week', {
      params: { divisionId, weekStart, ...(teamId ? { teamId } : {}) },
    }),

  getDirective: (divisionId: string, date: string) =>
    api.get<{ directive: Directive | null }>('/tasks/directives', { params: { divisionId, date } }),

  setDirective: (divisionId: string, workDate: string, body: string) =>
    api.post<{ directive: Directive }>('/tasks/directives', { divisionId, workDate, body }),

  addUrgent: (body: {
    divisionId: string; assigneeId: string; topic: string;
    brief?: string; deadlineAt?: string; workDate?: string;
  }) => api.post<{ point: TaskPoint; taskId: string }>('/tasks/urgent', body),

  remind: (pointId: string, body: { userId?: string; note?: string } = {}) =>
    api.post<{ sent: boolean; to: string }>(`/tasks/points/${pointId}/remind`, body),

  notDone: (pointId: string, reason: string) =>
    api.post<{ status: string }>(`/tasks/points/${pointId}/not-done`, { reason }),

  withdraw: (pointId: string, reason: string) =>
    api.post<{ status: string }>(`/tasks/points/${pointId}/withdraw`, { reason }),

  // ── недельная задача ───────────────────────────────────────────────
  getWeekly: (divisionId: string) =>
    api.get<{ weeklyTasks: any[] }>('/tasks/weekly', { params: { divisionId } }),

  createWeekly: (body: { divisionId: string; title: string; description?: string; weekStart?: string }) =>
    api.post<{ weeklyTask: any }>('/tasks/weekly', body),

  updateWeekly: (id: string, body: { title?: string; description?: string; targetCount?: number }) =>
    api.patch<{ weeklyTask: any }>(`/tasks/weekly/${id}`, body),

  // ── дневные задачи ─────────────────────────────────────────────────
  getTasks: (divisionId: string, date?: string) =>
    api.get<{ tasks: any[] }>('/tasks', { params: { divisionId, ...(date ? { date } : {}) } }),

  createTask: (body: {
    divisionId: string; assigneeId: string; workDate: string; teamId?: string;
    projectId?: string; kind?: 'video' | 'simple'; weeklyTaskId?: string;
    points?: { topic: string; brief?: string; deadlineAt?: string }[];
  }) => api.post<{ task: any }>('/tasks', body),

  addPoint: (taskId: string, body: { topic: string; brief?: string; deadlineAt?: string }) =>
    api.post<{ point: TaskPoint }>(`/tasks/${taskId}/points`, body),

  closeDay: (taskId: string, justification?: string) =>
    api.post<{ task: any }>(`/tasks/${taskId}/close`, { justification }),

  markDone: (pointId: string, attachmentPath?: string) =>
    api.post<{ status: string }>(`/tasks/points/${pointId}/done`, { attachmentPath }),

  // ── документы ──────────────────────────────────────────────────────
  createFileUrl: (pointId: string, purpose: 'brief' | 'attachment', fileName: string) =>
    api.post<{ bucket: string; path: string; token: string; signedUrl: string }>(
      `/tasks/points/${pointId}/file-url`, { purpose, fileName },
    ),

  setFile: (pointId: string, purpose: 'brief' | 'attachment', path: string) =>
    api.patch<Record<string, string>>(`/tasks/points/${pointId}/file`, { purpose, path }),

  getFileUrl: (pointId: string, purpose: 'brief' | 'attachment' = 'brief') =>
    api.get<{ url: string; path: string }>(`/tasks/points/${pointId}/file-url`, { params: { purpose } }),

  // ── телеграм ───────────────────────────────────────────────────────
  getMembers: (divisionId: string) =>
    api.get<{ members: { id: string; name: string | null; role: DivisionRole; reportsTo: string | null }[] }>(
      '/tasks/members', { params: { divisionId } },
    ),

  telegramStatus: () => api.get<{ linked: boolean; username: string | null }>('/tasks/telegram/status'),
  telegramLink: () => api.post<{ url: string; expiresAt: string }>('/tasks/telegram/link', {}),

  createUploadUrl: (pointId: string, fileName: string) =>
    api.post<UploadTicket>(`/tasks/points/${pointId}/upload-url`, { fileName }),

  attachVideo: (
    pointId: string,
    body: { title: string; storagePath: string; sizeBytes?: number; durationSeconds?: number | null },
  ) => api.post<{ video: { id: string } }>(`/tasks/points/${pointId}/video`, body),

  setVideoTitle: (pointId: string, title: string) =>
    api.patch<{ title: string }>(`/tasks/points/${pointId}/video`, { title }),

  addOwnTopic: (taskId: string, topic: string) =>
    api.post<{ point: TaskPoint }>(`/tasks/${taskId}/own-topic`, { topic }),
};

/**
 * Кладёт файл в Storage по подписанной ссылке.
 *
 * Не через `fetch`: нужен прогресс, а `fetch` его не отдаёт. XHR — здесь
 * единственный способ показать полосу, а ролик на сотню мегабайт без полосы
 * выглядит как зависший интерфейс.
 */
export function uploadToSignedUrl({
  ticket,
  file,
  onProgress,
  signal,
}: {
  ticket: UploadTicket;
  file: File;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Загрузка отменена', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', ticket.signedUrl, true);
    xhr.setRequestHeader('x-upsert', 'true');
    if (file.type) xhr.setRequestHeader('content-type', file.type);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(Math.round((event.loaded / event.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error(errorTextOf(xhr) ?? `Загрузка не удалась (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Сеть оборвалась во время загрузки'));
    xhr.onabort = () => reject(new DOMException('Загрузка отменена', 'AbortError'));

    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

function errorTextOf(xhr: XMLHttpRequest): string | null {
  try {
    const body = JSON.parse(xhr.responseText) as { message?: string; error?: string };
    return body.message ?? body.error ?? null;
  } catch {
    return null;
  }
}

/**
 * Длительность ролика — из самого файла, до отправки.
 *
 * Сервер её не считает: ffmpeg в edge-функции нет, а тянуть файл обратно ради
 * одной цифры дороже, чем спросить у браузера, который его и так открыл.
 */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}
